/**
 * Machine states that must NOT blank the page with the full-page `<Loader>`
 * (see `WithLoadingState` in `router.tsx`). These are "idle-ish" states where
 * either nothing is loading or a background async op is running that should
 * leave the current page interactive.
 *
 * PSTUDIO-3625: `reloadingNodesFlow` is the SILENT cross-tab reload state
 * (entered only when `reloadNodes` fires with `payload.silent === true`, see
 * the guarded transition in `prostudio.xstate.ts`). It runs `mergeVersionData`
 * to patch rows in place, so the receiving tab must stay interactive — like a
 * Google Sheets live edit — instead of showing the page-level spinner. CSV
 * import uses the SEPARATE `uploadingCsvFileFlow` state, which is intentionally
 * NOT listed here so it keeps its loader during the full-rebuild import.
 *
 * Kept in a dependency-light module (no React / no `app.xstate`) so it can be
 * unit-tested in the `node` vitest env without instantiating the machine
 * singleton. `as const` preserves the literal-union inference that
 * `useProStudioStateMatchesOneOf` relies on when the list is spread at the
 * call site.
 */
/**
 * The one state in which the Pro Studio machine accepts a channel mutation.
 *
 * PSTUDIO-3897: `createChannel` / `updateChannel` are handled ONLY under `idle`
 * (see the `on:` block of `idle` in `prostudio.xstate.ts`). Anywhere else they
 * bubble to `projectDataLoaded`, whose handler just calls `event.payload
 * .errorCb?.()` — the mutation never runs. Callers that need to know "can the
 * machine take this right now?" must match THIS path; `state.can({ type:
 * 'updateChannel' })` is a trap, because that bubble-up handler makes `can()`
 * report `true` while the machine is busy.
 *
 * Exported so `wait-for-pro-studio-idle.ts` and the channel forms share one
 * literal with the entry below instead of re-typing the path.
 */
export const PROJECT_DATA_LOADED_IDLE_STATE =
  'checkingUserInfo.projectFlow.projectDataLoaded.idle';

export const NON_LOADING_STUDIO_STATES = [
  'studio_idle',
  'accessDenied',
  'error',
  // PSTUDIO-3671: terminal "solution was deleted" state (sibling of
  // accessDenied/error). On the non-reload eject paths the machine parks here
  // while Root redirects to All Solutions; without this entry Home
  // (WithLoadingState) would blank the solutions list behind the full-page
  // <Loader> until the next manual navigation, since the same-project redirect
  // dispatches no setParams to move the machine out.
  'solutionNotFound',
  PROJECT_DATA_LOADED_IDLE_STATE,
  'checkingUserInfo.projectFlow.projectDataLoaded.botFlow.deletingScriptFlow',
  'checkingUserInfo.projectFlow.projectDataLoaded.botFlow.loadingScriptFlow',
  'checkingUserInfo.projectFlow.projectDataLoaded.botFlow.botFlowError',
  'checkingUserInfo.projectFlow.projectDataLoaded.botFlow.updatingSolutionFlow',
  'checkingUserInfo.projectFlow.projectDataLoaded.botFlow.downloadingVersionFileFlow',
  'checkingUserInfo.projectFlow.projectDataLoaded.botFlow.reloadingNodesFlow',
  // Live Agent Platforms CRUD runs through these flows; keep the Project
  // Settings page mounted (don't show the full-page loader) so its own
  // load-on-mount effect doesn't re-fire and loop.
  'checkingUserInfo.projectFlow.projectDataLoaded.botFlow.loadingLiveAgentPlatformsFlow',
  'checkingUserInfo.projectFlow.projectDataLoaded.botFlow.creatingLiveAgentPlatformFlow',
  'checkingUserInfo.projectFlow.projectDataLoaded.botFlow.updatingLiveAgentPlatformFlow',
  'checkingUserInfo.projectFlow.projectDataLoaded.botFlow.deletingLiveAgentPlatformFlow',
  // Reporting API Credentials + Advanced Settings (Error Handling) CRUD —
  // same reasoning: keep the Project Settings page mounted during these flows.
  'checkingUserInfo.projectFlow.projectDataLoaded.botFlow.loadingApiCredentialsFlow',
  // The channel-scoped counterpart (PSTUDIO-4253): same step of the same
  // Channel Settings chain, so it must be non-loading for the same reason —
  // blanking the page here unmounts the form and re-fires the chain.
  'checkingUserInfo.projectFlow.projectDataLoaded.botFlow.loadingChannelApiCredentialsFlow',
  'checkingUserInfo.projectFlow.projectDataLoaded.botFlow.creatingApiCredentialFlow',
  'checkingUserInfo.projectFlow.projectDataLoaded.botFlow.updatingApiCredentialFlow',
  'checkingUserInfo.projectFlow.projectDataLoaded.botFlow.deletingApiCredentialFlow',
  'checkingUserInfo.projectFlow.projectDataLoaded.botFlow.loadingErrorHandlingFlow',
  'checkingUserInfo.projectFlow.projectDataLoaded.botFlow.updatingErrorHandlingFlow',
  // Default Messages (autoresponders) CRUD — same reasoning.
  'checkingUserInfo.projectFlow.projectDataLoaded.botFlow.loadingDefaultMessagesFlow',
  'checkingUserInfo.projectFlow.projectDataLoaded.botFlow.updatingDefaultMessagesFlow',
  // Data Privacy (masking rule sets) CRUD — same reasoning.
  'checkingUserInfo.projectFlow.projectDataLoaded.botFlow.loadingPypeRuleGroupsFlow',
  'checkingUserInfo.projectFlow.projectDataLoaded.botFlow.creatingPypeRuleGroupFlow',
  'checkingUserInfo.projectFlow.projectDataLoaded.botFlow.updatingPypeRuleGroupFlow',
  'checkingUserInfo.projectFlow.projectDataLoaded.botFlow.deletingPypeRuleGroupFlow',
  'checkingUserInfo.projectFlow.projectDataLoaded.botFlow.assigningPypeRuleGroupFlow',
  'checkingUserInfo.projectFlow.projectDataLoaded.botFlow.assigningPypeConnectionFlow',
] as const;

export type PageLoadView = 'loading' | 'user-info-error' | 'ready';

/**
 * What `WithLoadingState` (router.tsx) shows for a page.
 *
 * Two ways the user's data fails to arrive, and either one wins over `loading`,
 * because in both `useLoadingState` stays true for good:
 * - PSTUDIO-4099 S30: `userInfoFailed` (the user machine in `loggedIn.error`).
 *   `getUserData` returns early without `ctx.user.id`, so `GetUserProjects` is
 *   never sent and `userProjects` never arrives.
 * - PSTUDIO-4492 item 6: `userProjectsFailed` (`ctx.userProjectsLoadFailed`).
 *   `GetUserProjects` was sent and failed, so `userProjects` never arrives either.
 * Both end on the same screen and the same retry, `user.refetch`.
 */
export const resolvePageLoadView = ({
  loading,
  userInfoFailed,
  userProjectsFailed = false,
}: {
  loading: boolean;
  userInfoFailed: boolean;
  userProjectsFailed?: boolean;
}): PageLoadView => {
  if (userInfoFailed || userProjectsFailed) return 'user-info-error';

  return loading ? 'loading' : 'ready';
};
